import { Order, SortParams } from "@/types/types";

/**
 * Effective default sort (recency) the data-loads fall back to when nothing is explicitly selected
 * — mirrors the list slice's initial `sortingInbox`. On the smart-ordered surfaces (Outbox/Inbox)
 * the backend treats this as its schedule/paused/replies-aware ordering (PREC-3544).
 */
export const DEFAULT_SORT_BY = "updatedAt";
export const DEFAULT_SORT_ORDER = Order.DESC;

/**
 * URL sentinel for the explicitly-chosen **"Default"** option (distinct from "nothing selected" so
 * pages load with no sort highlighted, yet the default can still be picked). Resolves to
 * {@link DEFAULT_SORT_BY} for the data-load; the backend never sees this value.
 */
export const DEFAULT_SORT_KEY = "default";

/**
 * The dedicated "best reply" sort sentinel (PREC-3599), resolved server-side to a reply-sentiment
 * rank (INTERESTED first). Offered only on the Inbox via {@link SortByDropdownProps.extraMostUsed}.
 */
export const BEST_REPLY_SORT_KEY = "bestReply";

/** A single selectable sort target rendered in the dropdown. */
export interface SortOption {
	/** Backend sort key (the column's `orderTitle`, e.g. `organization.name`). */
	orderTitle: string;
	/** Human-readable, already-translated label. */
	title: string;
}

export interface SortByDropdownProps {
	/** Currently active sort key (`sortingInbox.sortBy`). */
	sortBy: string;
	/** Currently active sort direction (`sortingInbox.sortOrder`). */
	sortOrder: SortParams["sortOrder"];
	/** Apply a new sort — wire to the same dispatch the column headers use. */
	onSort: (params: SortParams) => void;
	/**
	 * Visible sortable columns for the alphabetical section. Pass the currently visible columns
	 * only (the list changes as the user toggles column visibility). Card surfaces with no column
	 * table (Inbox/Outbox/Compose/Review) pass none — only "Most used" shows there.
	 */
	columnOptions?: SortOption[];
	/**
	 * Surface-specific extra "Most used" sorts appended after the shared {@link MOST_USED_SORTS}
	 * (already translated). Used by the Inbox to add "Best reply" without leaking it to other lists.
	 */
	extraMostUsed?: SortOption[];
	/** Extension/shadow-root portal target; defaults to `document.body` on the web. */
	portalContainer?: HTMLElement | null;
	testId?: string;
}

/**
 * The fixed "Most used" sort targets (PREC-3544), in display order. `orderTitle` values are the
 * same backend sort keys the columns expose; `leadScore` is the dedicated lead-score sentinel
 * resolved server-side (sortable even when no Lead Priority column is shown). `key` indexes the
 * i18n labels under `list.sortByOptions.*`.
 */
export const MOST_USED_SORTS: { key: string; orderTitle: string }[] = [
	{ key: "lastAddedEnriched", orderTitle: "list_person.updatedAt" },
	{ key: "leadPriority", orderTitle: "leadScore" },
	{ key: "companyName", orderTitle: "organization.name" },
	{ key: "companyCategory", orderTitle: "score_company_category_result.result" },
	{ key: "personaCategory", orderTitle: "score_persona_category_result.result" },
	{ key: "jobTitle", orderTitle: "job_title_to_seniority.jobTitle" },
	{ key: "jobLevel", orderTitle: "job_title_to_seniority.seniority" },
	{ key: "employeeSize", orderTitle: "organization.estimatedNumEmployees" },
];
